import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { ItemTypes } from '../../utils/dragTypes';
import { getModuleIconComponent } from '../../utils/moduleUtils';
import { MODULE_CAPABILITIES } from '../../moduleConfig';
import { shadeColor, toRgba } from '../../../../ui/theme/colors';
import theme from '../../../../ui/theme';
import { Flex, Icon, Typo } from '../../../../ui';

// Drop scale-into-place animation
const dropScale = keyframes`
  0% {
    transform: scale(1.08);
    opacity: 0.8;
  }
  50% {
    transform: scale(0.98);
  }
  75% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Drag handle icon
const DragHandleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.4;
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

// Module block container
const ModuleBlockContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px 12px 2px;
  background: ${({ $bgColor, $isRecentlyDropped, $themeColor }) => ($isRecentlyDropped ? shadeColor($themeColor, 0.85) : $bgColor || theme.colors.NEU_0)};
  border: 2px solid
    ${({ $borderColor, $isDragging, $isRecentlyDropped, $themeColor }) =>
      $isDragging ? 'transparent' : $isRecentlyDropped ? $themeColor : $borderColor || theme.colors.NEU_200};
  border-radius: 8px;
  cursor: grab;
  transition: all 0.2s ease;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.4 : 1)};
  flex: 1;
  min-width: 0;
  animation: ${({ $isRecentlyDropped }) => ($isRecentlyDropped ? dropScale : 'none')} 0.4s ease-out;

  /* Prevent children from intercepting drag events */
  & > * {
    pointer-events: none;
  }

  &:hover {
    border-color: ${({ $hoverBorderColor }) => $hoverBorderColor};
    box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.08)};

    ${DragHandleIcon} {
      opacity: 1;
    }
  }

  &:active {
    cursor: grabbing;
  }
`;

// Module info section
const ModuleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  pointer-events: none;
`;

/**
 * OrganizerModuleBlock - Draggable module block for the layout organizer
 */
const OrganizerModuleBlock = ({ module, themeColor, isRecentlyDropped }) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);

  const moduleType = module?.type?.name;
  const IconComponent = getModuleIconComponent(moduleType);
  const labelKey = MODULE_CAPABILITIES[moduleType]?.labelKey;
  const moduleLabel = labelKey ? t(labelKey) : moduleType;

  const [{ isDraggingDnd }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.MODULE,
      item: {
        moduleId: module.id,
        moduleType,
        rowId: module.pageRowId,
      },
      collect: (monitor) => ({
        isDraggingDnd: monitor.isDragging(),
      }),
    }),
    [module.id, moduleType, module.pageRowId],
  );

  // Hide default drag preview
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Track drag state
  useEffect(() => {
    if (isDraggingDnd && !isDragging) {
      setIsDragging(true);
    } else if (!isDraggingDnd && isDragging) {
      setIsDragging(false);
    }
  }, [isDraggingDnd, isDragging]);

  const bgColor = shadeColor(themeColor, 0.95);
  const borderColor = shadeColor(themeColor, 0.8);
  const hoverBorderColor = shadeColor(themeColor, 0.5);

  return (
    <ModuleBlockContainer
      ref={drag}
      $bgColor={bgColor}
      $borderColor={borderColor}
      $hoverBorderColor={hoverBorderColor}
      $isDragging={isDragging}
      $isRecentlyDropped={isRecentlyDropped}
      $themeColor={themeColor}
    >
      <DragHandleIcon>
        <Icon.DragIndicator size='small' color={themeColor} />
      </DragHandleIcon>
      <Flex direction='column' width='100%' align='center'>
        <IconComponent size='small' color={themeColor} />
        <ModuleInfo>
          <Typo.Info fontWeight='600' color={themeColor} $caps>
            {moduleLabel}
          </Typo.Info>
        </ModuleInfo>
      </Flex>
    </ModuleBlockContainer>
  );
};

export default OrganizerModuleBlock;
